// The agent profile's tabs the Agents UX pass (2026-10-21) adds beside M3-08's Runs, Approvals and
// Budget (de-tabs.tsx, budgets-screen.tsx): Skills (the skill packs the seat's role mounts),
// and the Activity and Approvals of a company whose `agents` add-on is off (one plain line under
// the banner, no sample rows). Skills reads the seat (its role) and the cached skill-pack list.
// INC2-CONTACT: Connections moved to features/connections/de-connections.tsx (the grants the DE
// holds and its last acts).
import { Badge, buttonVariants, cn, EmptyState, ListRow } from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PackageIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isManager } from "../company";
import { seatQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { skillPacksPaths, skillPacksQuery } from "./skill-packs-screen";

/** Skills: the skill packs the seat's role mounts into this agent's runs (M4-02). */
export function DeSkillsTab({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const seat = useQuery({
    ...seatQuery(api, company.id, de.seat?.id ?? ""),
    enabled: de.seat !== null,
  });
  const packs = useInfiniteQuery(skillPacksQuery(api, company.id));
  const roleId = seat.data?.role_id;
  const rows = (packs.data?.pages.flatMap((p) => p.data) ?? []).filter(
    (p) => roleId !== undefined && p.role_ids.includes(roleId),
  );
  const manage = isManager(company) ? (
    <Link
      to={skillPacksPaths(company).list}
      params={{ slug: company.slug }}
      search={keepFocus}
      className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
    >
      Assign packs
    </Link>
  ) : null;
  if (!de.seat)
    return (
      <p className="text-sm text-muted-foreground">
        Skill packs are mounted per seat, and this agent has no active seat.
      </p>
    );
  if (seat.isPending || packs.isPending) return <Loading label="Loading skills" rows={2} />;
  if (seat.isError) return <LoadError what="The seat" error={seat.error} />;
  if (packs.isError) return <LoadError what="Skill packs" error={packs.error} />;
  if (!rows.length)
    return (
      <EmptyState
        variant="compact"
        icon={PackageIcon}
        title="No skill packs on this seat's role"
        description="A pack assigned to the role is mounted into every run of its seats."
        actions={manage}
      />
    );
  return (
    <div className="flex min-w-0 flex-col gap-3" data-slot="de-skills">
      <div data-slot="de-skill-packs" className="rounded-lg border border-border">
        {rows.map((p) => (
          <ListRow
            key={p.id}
            data-row
            data-pack={p.id}
            lead={<PackageIcon aria-hidden="true" />}
            title={p.name}
            render={
              <Link
                to={skillPacksPaths(company).pack}
                params={{ slug: company.slug, id: p.id }}
                search={keepFocus}
              />
            }
            chips={
              <>
                <Badge variant="outline">v{p.version}</Badge>
                {p.status !== "published" ? <Badge variant="warning">{p.status}</Badge> : null}
              </>
            }
            meta={p.description}
          />
        ))}
      </div>
      {manage ? <div>{manage}</div> : null}
    </div>
  );
}

/** A tab of the `agents` add-on while it is off: one plain line (the banner says who turns it on). */
function OffLine({ slot, children }: { slot: string; children: string }) {
  return (
    <p className="text-sm text-muted-foreground" data-slot={slot}>
      {children}
    </p>
  );
}

/** The Activity tab of a company whose `agents` add-on is off. */
export function DeRunsOff({ name }: { name: string }) {
  return <OffLine slot="de-activity-off">{`${name}'s runs show here once Agents is on.`}</OffLine>;
}

/** The Skills tab of a company whose `agents` add-on is off (skill packs are an add-on route). */
export function DeSkillsOff({ name }: { name: string }) {
  return (
    <OffLine slot="de-skills-off">{`The skill packs ${name} runs show here once Agents is on.`}</OffLine>
  );
}

/** The Approvals tab of a company whose `agents` add-on is off. */
export function DeApprovalsOff({ name }: { name: string }) {
  return (
    <OffLine slot="de-approvals-off">{`What ${name} asks before it acts shows here once Agents is on.`}</OffLine>
  );
}
